import { AvatarAnimator } from '@/webGL/objects/AvatarAnimator'

import { Loader } from '@/utils/Loader'
// import { gui } from '@/utils/gui'
import { randomRange } from '@/utils/Maths'

import gsap from 'gsap'

import { Vector3 } from 'three'

const mouthExpressions = ['Mouth_A', 'Mouth_O', 'LHalfSmile']
const eyesExpressions = ['REyeClosed', 'LEyeHalfClosed', 'REyeHalfClosed', 'LEyeLWLidUp', 'REyeLWLidUp']
const aliveExpressions = ['LEyebrowUp', 'REyebrowUp', 'LEyebrowDown', 'REyebrowDown', 'LEyeWideOpen', 'LEyeClosed', 'MouthSmileOpen']

export class Avatar extends AvatarAnimator {
  constructor (url, onBaseActions, onAdditiveActions) {
    super(onBaseActions, onAdditiveActions)

    this.onObjectReady = this.onObjectReady.bind(this)
    this.talk = this.talk.bind(this)
    this.beAlive = this.beAlive.bind(this)
    this.blink = this.blink.bind(this)
    this.onTalk = this.onTalk.bind(this)
    this.onStopTalk = this.onStopTalk.bind(this)
    this.treatMaterial = this.treatMaterial.bind(this)

    // Side boolean
    this.isLeft = true
    // used to know wich base animation to use
    this.isTalking = false

    this.promise = Loader.load(url, false, obj => {
      obj.receiveShadow = true
      obj.castShadow = true
    })

    this.promise.then(this.onObjectReady)

    // const guiShininess = gui.addFolder('Shininess')
    // guiShininess.add(this, 'shininess').min(0).max(1).step(0.01)
  }

  onObjectReady (obj) {
    this.obj = obj

    this.add(this.obj)

    this._onObjectReady(obj)

    this.obj.traverse(child => {
      if (child.isMesh) {
        if (Array.isArray(child.material)) {
          child.material.forEach(material => {
            if (material.shininess) {
              material.baseShininess = material.shininess
            }
          })
        }
        else {
          child.material.baseShininess = child.material.shininess
        }
      }
    })

    this.shininess = .2

    setInterval(() => {
      const center = new Vector3()
      this.boundingBox.getCenter(center)
    }, 1000)

    this.initMorphing()
  }

  initMorphing () {
    this.face = this.obj.getObjectByName('Face')

    const expressions = Object.keys( this.face.morphTargetDictionary )

    this.expressions = expressions.map((expression, index) => {
      return {
        name: expression,
        index: index,
        value: 0,
        isFace: mouthExpressions.includes(expression),
        isEyes: eyesExpressions.includes(expression),
        isAlive: aliveExpressions.includes(expression)
      }
    })

    this.beAlive(this.expressions.filter(expression => expression.isAlive))
    this.blink(this.expressions.filter(expression => expression.isEyes))
  }

  talk (expressions) {
    const rand = randomRange(.05, .2)

    expressions.forEach((expression, index) => {
      if (Math.random() > .5) {
        gsap.to(expression, {
          value: randomRange(.5, .9),
          duration: rand,
          onUpdate: val => {
            this.face.morphTargetInfluences[expression.index] = expression.value
          }
        })
      }
      else {
        gsap.to(expression, {
          value: 0,
          duration: rand,
          onUpdate: val => {
            this.face.morphTargetInfluences[expression.index] = expression.value
          }
        })
      }
    })

    this.talkTimeout = setTimeout(() => {
      this.talk(expressions)
    }, rand * 1000 + 50)
  }

  onTalk () {
    this.isTalking = true
    this.talk(this.expressions.filter(expression => expression.isFace))
  }

  onStopTalk () {
    clearTimeout(this.talkTimeout)
    this.isTalking = false

    this.expressions.filter(expression => expression.isFace).forEach((expression, index) => {
      gsap.killTweensOf(expression) // Prevent from being caught with other easings
      gsap.to(expression, {
        value: 0,
        duration: .05,
        onUpdate: val => {
          this.face.morphTargetInfluences[expression.index] = expression.value
        }
      })
    })
  }

  beAlive (expressions) {
    const rand = randomRange(.5, 2)

    expressions.forEach((expression, index) => {
      if (Math.random() > .5) {
        gsap.to(expression, {
          value: Math.random() * .5,
          duration: rand,
          onUpdate: val => {
            this.face.morphTargetInfluences[expression.index] = expression.value
          },
          onComplete: () => {
            gsap.to(expression, {
              value: 0,
              duration: rand,
              onUpdate: val => {
                this.face.morphTargetInfluences[expression.index] = expression.value
              }
            })
          }
        })
      }
    })

    setTimeout(() => {
      this.beAlive(expressions)
    }, (rand * 1000) * 2 + randomRange(100, 2000))
  }

  blink (expressions) {
    expressions.forEach((expression, index) => {
      gsap.to(expression, {
        value: 1,
        duration: .15,
        onUpdate: val => {
          this.face.morphTargetInfluences[expression.index] = expression.value
        },
        onComplete: () => {
          gsap.to(expression, {
            value: 0,
            duration: .15,
            onUpdate: val => {
              this.face.morphTargetInfluences[expression.index] = expression.value
            }
          })
        }
      })
    })

    setTimeout(() => {
      this.blink(expressions)
    }, randomRange(5000, 10000))
  }

  treatMaterial (material) {
    if (material.shininess) {
      material.shininess = material.baseShininess * this._shininess
    }
  }

  set shininess (value) {
    this._shininess = value

    this.obj.traverse(child => {
      if (child.isMesh) {
        if (Array.isArray(child.material)) {
          child.material.forEach(this.treatMaterial)
        }
        else {
          this.treatMaterial(child.material)
        }
      }
    })

    this._shininess = value
  }

  get shininess () {
    return this._shininess
  }

  destroy() {
    this.remove(this.obj)
    this.onDestroy()
  }
}